// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { debounce, isEqual } from 'lodash';

import { isValueEmpty } from '../../../../utils/Utils';
import TableSchemaBuilder from '../../../TableSchemaBuilder/TableSchemaBuilder';
import ConfigColumnTableView from './ConfigColumnTableView';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';

const AdvancedTableRenderer = ({
  attribute,
  onChange,
  value,
  isViewMode = false,
  entityData = EMPTY_OBJECT,
  usersList = EMPTY_ARRAY,
  teamUsersList = EMPTY_ARRAY,
  groupList = EMPTY_ARRAY,
}) => {
  const lastSentValueRef = useRef(null);

  // ── Config-column mode ──────────────────────────────────────────────────
  const isConfigColumnMode = useMemo(
    () => !!attribute?.advanced_table_schema?.config_column,
    [attribute]
  );

  // ── Debounced onChange for the normal spreadsheet mode ───────────────────
  const debounceAdvanceTableValue = useMemo(
    () =>
      debounce((parameterName, parameterValue) => {
        onChange(parameterValue);
        lastSentValueRef.current = parameterValue;
      }, 500),
    [onChange]
  );

  useEffect(() => {
    return () => {
      debounceAdvanceTableValue.cancel();
    };
  }, [debounceAdvanceTableValue]);

  const handleTableChange = useCallback(
    (newSchema) => {
      if (!isViewMode) {
        const extractedCells = {
          Cells: Object?.keys(newSchema?.cells || {}).reduce((acc, cellKey) => {
            if (newSchema?.cells?.[cellKey]?.value !== undefined) {
              // eslint-disable-next-line no-param-reassign
              acc[cellKey] = { value: newSchema?.cells?.[cellKey]?.value };
            }
            return acc;
          }, {}),
        };

        if (!isEqual(extractedCells, lastSentValueRef.current)) {
          debounceAdvanceTableValue(
            attribute?.name || 'advancedTable',
            extractedCells
          );
        }
      }
    },
    [isViewMode, attribute?.name, debounceAdvanceTableValue]
  );

  const advancedTableSchema = useMemo(
    () => attribute?.advanced_table_schema,
    [attribute]
  );

  const attributeValue = useMemo(() => value || {}, [value]);

  // The all cells are calculated because we are storing cells data in table schema and data can be different.
  // e.g. If someone does not add anything in any one of the cells in the table schema and then in model we
  // added data in the cell. So in table schema we did not have that cell registered but in models data we
  // have that cell. So we need to merge the data from both table schema and model cells so we can render
  // all sides cells.
  const allCells = useMemo(() => {
    const keysOfSchemaCells = Object.keys(advancedTableSchema?.cells || {});
    const keysOfDataSavedInModel = Object.keys(attributeValue?.Cells || {});
    return Array.from(
      new Set([...keysOfSchemaCells, ...keysOfDataSavedInModel])
    );
  }, [advancedTableSchema?.cells, attributeValue?.Cells]);

  const mergedSchema = useMemo(() => {
    // If the attribute value is empty, return the advanced table schema as-is.
    if (isValueEmpty(attributeValue)) {
      return advancedTableSchema;
    }
    return {
      ...advancedTableSchema,
      cells: allCells.reduce((acc, cellKey) => {
        const schemaCell = advancedTableSchema?.cells?.[cellKey] || {};

        const updatedAcc = {
          ...acc,
          [cellKey]: {
            ...schemaCell,
            value: isValueEmpty(attributeValue?.Cells?.[cellKey]?.value)
              ? schemaCell?.value
              : attributeValue?.Cells?.[cellKey]?.value,
          },
        };

        return updatedAcc;
      }, {}),
    };
  }, [advancedTableSchema, attributeValue, allCells]);

  // ── Config-column mode — delegate to ConfigColumnTableView ───────────────
  if (isConfigColumnMode) {
    return (
      <Box sx={{ width: '100%' }}>
        <ConfigColumnTableView
          attribute={attribute}
          value={attributeValue}
          onChange={onChange}
          isViewMode={isViewMode}
          entityData={entityData}
          usersList={usersList}
          teamUsersList={teamUsersList}
          groupList={groupList}
          configColumn={isConfigColumnMode}
        />
      </Box>
    );
  }

  // ── Standard spreadsheet mode ────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%' }}>
      <TableSchemaBuilder
        initialSchema={mergedSchema}
        onSchemaChange={handleTableChange}
        tableConfig={{
          showToolbar: false,
          readOnly: isViewMode,
          editMode: !isViewMode,
        }}
      />
    </Box>
  );
};

AdvancedTableRenderer.propTypes = {
  attribute: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    advanced_table_schema: PropTypes.shape({
      cells: PropTypes.shape({}),
      config_column: PropTypes.bool,
      columns: PropTypes.number,
      columnSettings: PropTypes.shape({}),
    }),
  }).isRequired,
  value: PropTypes.shape({
    Cells: PropTypes.shape({}),
    mergedCells: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isViewMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  entityData: PropTypes.shape({
    entity_id: PropTypes.string,
    entity_type: PropTypes.string,
  }),
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
};

export default AdvancedTableRenderer;
